import { motion } from "framer-motion";
import { Mail, MessageCircle, Phone } from "lucide-react";
import { BRAND } from "@/content/site";
import { waHref } from "@/content/media";
import { useLang } from "@/lang";

const EASE = [0.22, 1, 0.36, 1];

export default function FinalCta() {
  const { t, isEs } = useLang();
  const goldWords = isEs ? ["Sí."] : ["Yes."];
  const words = t.cta.tagline.split(" ");

  return (
    <section data-testid="final-cta-band" className="relative overflow-hidden bg-navy py-24 sm:py-32">
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-0 h-[480px] w-[900px] max-w-full -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/10 blur-[120px]" />

      <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
        <motion.img
          initial={{ opacity: 0, scale: 0.85 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1, ease: EASE }}
          src="/logo-crest.png"
          alt="Equipment Concierge EC crest with horse"
          className="mx-auto h-20 w-20 rounded-full object-cover ring-1 ring-gold/60"
        />

        <h2 className="mt-10 font-serif text-4xl font-medium leading-[1.12] text-cream sm:text-6xl lg:text-7xl">
          {words.map((w, i) => (
            <span key={i} className="inline-block overflow-hidden pb-2 -mb-2">
              <motion.span
                initial={{ y: "110%" }}
                whileInView={{ y: "0%" }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.8, ease: EASE, delay: i * 0.07 }}
                className={`mr-[0.26em] inline-block ${goldWords.includes(w) ? "italic text-gold" : ""}`}
              >
                {w}
              </motion.span>
            </span>
          ))}
        </h2>

        <motion.p initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.8, ease: EASE, delay: 0.4 }} className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-cream/75 sm:text-lg">
          {t.cta.sub}
        </motion.p>

        <motion.p initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.8, ease: EASE, delay: 0.48 }} className="mt-4 font-serif text-2xl italic text-gold sm:text-3xl">
          {t.nico}
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.4 }} transition={{ duration: 0.8, ease: EASE, delay: 0.55 }} className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <a
            href={BRAND.phoneHref}
            data-testid="cta-call-btn"
            className="inline-flex items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-navy shadow-[0_14px_40px_rgba(201,164,92,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover sm:text-sm"
          >
            <Phone className="h-4 w-4" />
            {t.cta.call}
          </a>
          <a
            href={waHref(isEs ? "es" : "en")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-whatsapp-btn"
            className="inline-flex items-center justify-center gap-3 rounded-full border border-cream/35 px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-cream transition-all duration-300 hover:-translate-y-0.5 hover:border-wa hover:bg-wa hover:text-white sm:text-sm"
          >
            <MessageCircle className="h-4 w-4" />
            {t.cta.wa}
          </a>
          <a
            href={BRAND.emailHref}
            data-testid="cta-email-btn"
            className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-cream/80 underline-offset-4 transition-colors duration-300 hover:text-gold hover:underline"
          >
            <Mail className="h-4 w-4" />
            {t.cta.email}
          </a>
        </motion.div>
      </div>
    </section>
  );
}
